import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createMovie, updateMovie, getMovieById } from '../api/adminApi';
import { getAssetUrl } from '../../api/client';
import ImageUploadField from '../components/ImageUploadField';
import '../adminTheme.css';
import './adminForms.css';

const emptyForm = {
  title: '',
  status: 'now-showing',
  showTimes: '',
  releaseDate: '',
  language: '',
  genre: '',
  durationMinutes: '',
  order: 0,
  isActive: true,
};

export default function MovieFormPage() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { token } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [posterFile, setPosterFile] = useState(null);
  const [existingPoster, setExistingPoster] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEditing) return;
    getMovieById(id)
      .then((movie) => {
        setForm({
          title: movie.title || '',
          status: movie.status,
          showTimes: (movie.showTimes || []).join(', '),
          releaseDate: movie.releaseDate ? movie.releaseDate.slice(0, 10) : '',
          language: movie.language || '',
          genre: movie.genre || '',
          durationMinutes: movie.durationMinutes || '',
          order: movie.order || 0,
          isActive: movie.isActive,
        });
        setExistingPoster(getAssetUrl(movie.poster));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!isEditing && !posterFile) {
      setError('Please choose a poster image.');
      return;
    }
    if (form.status === 'now-showing' && !form.showTimes.trim()) {
      setError('Add at least one showtime.');
      return;
    }
    if (form.status === 'upcoming' && !form.releaseDate) {
      setError('Pick a release date.');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('status', form.status);
      if (form.status === 'now-showing') {
        formData.append('showTimes', form.showTimes);
      } else {
        formData.append('releaseDate', form.releaseDate);
      }
      if (form.language) formData.append('language', form.language);
      if (form.genre) formData.append('genre', form.genre);
      if (form.durationMinutes) formData.append('durationMinutes', form.durationMinutes);
      formData.append('order', form.order);
      formData.append('isActive', String(form.isActive));
      if (posterFile) formData.append('poster', posterFile);

      if (isEditing) {
        await updateMovie(token, id, formData);
      } else {
        await createMovie(token, formData);
      }
      navigate('/admin/movies');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: '#999' }}>Loading...</p>;

  return (
    <div className="cs-admin_form_page">
      <div className="cs-admin_page_header">
        <h1>{isEditing ? 'Edit Movie' : 'Add Movie'}</h1>
        <Link to="/admin/movies" className="cs-admin_btn cs-admin_btn-ghost">
          ← Back to list
        </Link>
      </div>

      {error && <div className="cs-admin_error">{error}</div>}

      <form onSubmit={handleSubmit} className="cs-admin_form">
        <label>Title *</label>
        <input value={form.title} onChange={(e) => updateField('title', e.target.value)} required />

        <label>Status *</label>
        <select value={form.status} onChange={(e) => updateField('status', e.target.value)}>
          <option value="now-showing">Now Showing</option>
          <option value="upcoming">Upcoming</option>
        </select>

        {form.status === 'now-showing' ? (
          <>
            <label>Showtimes * (comma separated)</label>
            <input
              value={form.showTimes}
              onChange={(e) => updateField('showTimes', e.target.value)}
              placeholder="10:00 AM, 1:30 PM, 6:00 PM"
            />
          </>
        ) : (
          <>
            <label>Release date *</label>
            <input
              type="date"
              value={form.releaseDate}
              onChange={(e) => updateField('releaseDate', e.target.value)}
            />
          </>
        )}

        <div className="cs-admin_form_row">
          <div>
            <label>Language</label>
            <input value={form.language} onChange={(e) => updateField('language', e.target.value)} />
          </div>
          <div>
            <label>Genre</label>
            <input value={form.genre} onChange={(e) => updateField('genre', e.target.value)} />
          </div>
          <div>
            <label>Duration (minutes)</label>
            <input
              type="number"
              value={form.durationMinutes}
              onChange={(e) => updateField('durationMinutes', e.target.value)}
            />
          </div>
        </div>

        <div className="cs-admin_form_row">
          <div>
            <label>Slider order</label>
            <input type="number" value={form.order} onChange={(e) => updateField('order', e.target.value)} />
          </div>
          <div className="cs-admin_checkbox_field">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
              />
              Active (visible on homepage)
            </label>
          </div>
        </div>

        <ImageUploadField
          label="Poster (recommended ~500x750)"
          existingImageUrl={existingPoster}
          onChange={setPosterFile}
          required={!isEditing}
        />

        <button type="submit" className="cs-admin_btn cs-admin_btn-primary" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Movie'}
        </button>
      </form>
    </div>
  );
}
